import Credit from "./Balance";
import Statistics from "./Statistics";
import Transactions from "./Transactions";
import FinancialVisualizations from "./FinancialVisualizations";
import Summary from "./Insights/Summary";
import {
  CompareTo,
  PERIODS,
  Period,
  Range,
  periodRange,
  previousPeriod,
  yearEarlier,
} from "./Insights/data";
import { remember, remembered } from "./Insights/useComparison";
import "./PaymentsTab.css";
import { useCallback, useEffect, useState, useMemo } from "react";
import {
  ApprovalStatus,
  FailedTransactionNoticeResponse,
  PaymentsService,
  TransactionType,
  AccountingSystemForCalculatingFinancialStatistics,
  RevenueBreakdown,
  CostOfTutoringBreakdown,
  OrganizationResponseForAdmins,
} from "client/openapi";
import moment from "moment-timezone";
import { ActivityLogIcon, BarChartIcon } from "@radix-ui/react-icons";
import TabNavbar from "components/TabNavbar";

import {
  StudentToOrgTransactionResponse,
  OrgToTadpoleTransactionResponse,
  TadpoleToTutorTransactionResponse,
  PayoutTransactionResponse,
  MeetingsService,
  MeetingListAdminResponse,
  MeetingStatus,
} from "client/openapi";
import TransactionSettings from "./TransactionSettings/TransactionSettings";
import Notifications from "util/notifications";
import { Dialog, DialogTrigger } from "components/Dialog";
import PendingPayrollRuns from "./NotifcationBanners/PendingPayrollRuns";
import { Button, ButtonColor } from "components/Button";
import FailedStudentToOrgTransactions from "./NotifcationBanners/FailedStudentToOrgTransactions";

export type ViewMode = "activity" | "insights";

const TABS: { value: string; icon: any; mode: ViewMode }[] = [
  { value: "Activity", icon: ActivityLogIcon, mode: "activity" },
  { value: "Insights", icon: BarChartIcon, mode: "insights" },
];

function OrgPayments({
  organization,
}: {
  organization: OrganizationResponseForAdmins;
}) {
  const [viewMode, setViewMode] = useState<ViewMode>("activity");

  // The period Activity and Insights share: a preset counted from today in
  // the organization's zone, or custom dates. Each viewer's preset (not its
  // dates) is remembered; earlier choices carry over.
  const zone = organization.default_timezone || moment.tz.guess();
  // Today on every render, and again as the picker opens (it re-renders the
  // page), so a page left open overnight moves on.
  const today = moment.tz(zone).format("YYYY-MM-DD");
  const [, refreshToday] = useState(0);
  const earlier = remembered<string | null>(
    organization.id,
    "comparison",
    null
  );
  const [period, setPeriod] = useState<Period>(() =>
    remembered(
      organization.id,
      "period",
      earlier === "month"
        ? "this_month"
        : earlier === "quarter"
        ? "this_quarter"
        : "last_6_months"
    )
  );
  const [customRange, setCustomRange] = useState<Range | null>(() =>
    remembered(organization.id, "customRange", null)
  );
  const range: Range =
    period === "custom" && customRange
      ? customRange
      : periodRange(period === "custom" ? "last_6_months" : period, today);
  const choosePeriod = (next: Period, custom?: Range) => {
    if (next === "custom" && custom) {
      setCustomRange(custom);
      remember(organization.id, "customRange", custom);
    }
    setPeriod(next);
    remember(organization.id, "period", next);
  };
  // Insights' comparison: No comparison on every visit, not remembered.
  const [compareTo, setCompareTo] = useState<CompareTo>("none");
  const [comparedPick, setComparedPick] = useState<Range | null>(null);
  const compared: Range | null =
    compareTo === "none"
      ? null
      : compareTo === "previous_year"
      ? yearEarlier(period, range)
      : compareTo === "custom" && comparedPick
      ? comparedPick
      : previousPeriod(period, range);
  const chooseCompareTo = (next: CompareTo) => {
    if (next === "custom")
      setComparedPick(compared ?? previousPeriod(period, range));
    setCompareTo(next);
  };
  // The period's days run from the organization's midnight to its next one.
  const rangeStart = useMemo(
    () => moment.tz(range[0], zone).startOf("day").toISOString(),
    [range[0], zone] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rangeUntil = useMemo(
    () => moment.tz(range[1], zone).endOf("day").toISOString(),
    [range[1], zone] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const [accountingSystem, setAccountingSystem] =
    useState<AccountingSystemForCalculatingFinancialStatistics>(
      AccountingSystemForCalculatingFinancialStatistics.TRANSACTIONS
    );

  const [transactionTypeFilter, setTransactionTypeFilter] = useState<
    TransactionType | "all"
  >("all");

  const [adminApprovalFilter, setAdminApprovalFilter] = useState<
    ApprovalStatus | "all"
  >("all");

  const [revenue, setRevenue] = useState<RevenueBreakdown>();
  const [cost, setCost] = useState<CostOfTutoringBreakdown>();
  const [financialsLoading, setFinancialsLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);

  // const options = { clientSecret: clientSecret };

  const [transactions, setTransactions] = useState<
    | (
        | StudentToOrgTransactionResponse
        | OrgToTadpoleTransactionResponse
        | TadpoleToTutorTransactionResponse
        | PayoutTransactionResponse
      )[]
    | MeetingListAdminResponse[]
  >([]);

  const [failedStudentToOrgTransactions, setFailedStudentToOrgTransactions] =
    useState<FailedTransactionNoticeResponse[]>([]);

  const [pendingPayrollRuns, setPendingPayrollRuns] = useState<
    OrgToTadpoleTransactionResponse[]
  >([]);

  const getTransactions = useCallback(async () => {
    setTransactionsLoading(true);
    try {
      if (
        accountingSystem ===
        AccountingSystemForCalculatingFinancialStatistics.TRANSACTIONS
      ) {
        const transactions_response =
          await PaymentsService.getTransactionsByOrg({
            orgId: organization.id,
            start: rangeStart,
            until: rangeUntil,
            accountingSystem: accountingSystem,
          });

        const filteredTransactions =
          transactionTypeFilter === "all"
            ? transactions_response
            : transactions_response.filter(
                (txn) => txn.transaction_type === transactionTypeFilter
              );

        setTransactions(
          filteredTransactions.sort((a, b) =>
            moment(b.timestamp).diff(moment(a.timestamp))
          )
        );
      } else if (
        accountingSystem ===
        AccountingSystemForCalculatingFinancialStatistics.MEETINGS
      ) {
        const meetings_response = await MeetingsService.getMeetingsByOrg({
          orgId: organization.id,
          start: rangeStart,
          until: rangeUntil,
          meetingAcceptedByAllTutors: MeetingStatus.ACCEPTED,
        });

        const filteredMeetings =
          adminApprovalFilter === "all"
            ? meetings_response
            : meetings_response.filter(
                (meeting) =>
                  meeting.reviewed_and_approved_by_admin === adminApprovalFilter
              );

        setTransactions(
          (filteredMeetings as MeetingListAdminResponse[]).sort((a, b) =>
            moment(b.start).diff(moment(a.start))
          )
        );
      }
    } finally {
      setTransactionsLoading(false);
    }
  }, [
    accountingSystem,
    organization.id,
    rangeStart,
    rangeUntil,
    transactionTypeFilter,
    adminApprovalFilter,
  ]);

  const revenueAndCostCache = useMemo(async () => {
    try {
      const [revenueResponse, costResponse] = await Promise.all([
        PaymentsService.getOrgRevenue({
          orgId: organization.id,
          start: rangeStart,
          until: rangeUntil,
          accountingSystem: accountingSystem,
          includeBreakdown: true,
        }),
        PaymentsService.getOrgCostOfTutoring({
          orgId: organization.id,
          start: rangeStart,
          until: rangeUntil,
          accountingSystem: accountingSystem,
          includeBreakdown: true,
        }),
      ]);

      return { revenue: revenueResponse, cost: costResponse };
    } catch (error) {
      console.error("Failed to fetch revenue and cost data:", error);
      Notifications.error("Failed to load financial data");
      return null;
    }
  }, [organization.id, rangeStart, rangeUntil, accountingSystem]);

  const getRevenueAndCost = useCallback(async () => {
    setFinancialsLoading(true);
    try {
      const result = await revenueAndCostCache;
      if (result) {
        setRevenue(result.revenue);
        setCost(result.cost);
      }
    } finally {
      setFinancialsLoading(false);
    }
  }, [revenueAndCostCache]);

  useEffect(() => {
    getTransactions();
    getRevenueAndCost();
  }, [getTransactions, getRevenueAndCost]);

  const getFailedStudentToOrgTransactions = useCallback(async () => {
    PaymentsService.getFailedTransactionNoticesByOrg({
      orgId: organization.id,
    })
      .then((response) => {
        setFailedStudentToOrgTransactions(response);
      })
      .catch((error) => {
        Notifications.error(error.message);
      });
  }, [organization.id]);

  const getPendingPayrollRuns = useCallback(async () => {
    PaymentsService.getOrgToTadpoleTransactionsReadyForPayroll({
      orgId: organization.id,
    })
      .then((response) => {
        setPendingPayrollRuns(response);
      })
      .catch((error) => {
        Notifications.error(error.message);
      });
  }, [organization.id]);

  useEffect(() => {
    getFailedStudentToOrgTransactions();
    getPendingPayrollRuns();
  }, [getFailedStudentToOrgTransactions, getPendingPayrollRuns]);

  const bar = {
    orgId: organization.id,
    period,
    range,
    presets: PERIODS.map((p) => ({ ...p, range: periodRange(p.value, today) })),
    onPeriod: choosePeriod,
    onPeriodOpen: () => refreshToday((n) => n + 1),
    rangeStart,
    rangeUntil,
    revenue: revenue?.revenue ?? 0,
    cost: cost?.cost ?? 0,
    basis: accountingSystem,
    onBasis: (basis: AccountingSystemForCalculatingFinancialStatistics) => {
      if (basis === accountingSystem) return; // already showing it
      setAccountingSystem(basis);
      setTransactions([]);
    },
  };

  const transactionsBlock = (
    <div className="mt-4 mx-4">
      {transactionsLoading ? (
        <div className="flex items-center justify-center bg-white rounded-lg border border-gray-200 h-48">
          <div
            className="spinner-border text-gray-400"
            role="status"
            aria-label="Loading transactions"
          />
          <span className="ml-3 text-gray-500 text-sm">Loading data...</span>
        </div>
      ) : (
        <Transactions
          transactions={transactions}
          accountingSystem={accountingSystem}
        />
      )}
    </div>
  );

  return (
    <div>
      {pendingPayrollRuns.length > 0 && (
        <div className="container mb-4 w-8/12">
          <div className="payroll-is-ready-to-run-block">
            <div className="tutor__profile_block-text">
              <span className="primsecond-text">Payroll is ready to run.</span>
              <p className="mt-1 secondary-text">
                Review the payments and click the trigger button to initiate the
                process.
              </p>
            </div>

            <Dialog>
              <DialogTrigger>
                <Button color={ButtonColor.GREEN}>View &rarr;</Button>
              </DialogTrigger>
              <PendingPayrollRuns pendingPayrollRuns={pendingPayrollRuns} />
            </Dialog>
          </div>
        </div>
      )}
      {failedStudentToOrgTransactions.length > 0 && (
        <div className="container mb-4 w-8/12">
          <div className="mt-4 failed-payments-block">
            <div className="tutor__profile_block-text">
              <span className="primsecond-text">
                One or more payments from students did not go through.
              </span>
              <p className="mt-1 secondary-text">
                Please review and retry them if needed.
              </p>
            </div>

            <Dialog>
              <DialogTrigger>
                <Button color={ButtonColor.PURPLE}>View &rarr;</Button>
              </DialogTrigger>
              <FailedStudentToOrgTransactions
                failedStudentToOrgTransactions={failedStudentToOrgTransactions}
                setFailedStudentToOrgTransactions={
                  setFailedStudentToOrgTransactions
                }
              />
            </Dialog>
          </div>
        </div>
      )}
      <div className="flex flex-col lg:flex-row lg:justify-between border-gray-300 border-b-2 rounded-t pb-4">
        <div>
          <Credit organization={organization} />
        </div>
      </div>
      <TabNavbar
        activeTab={TABS.find((tab) => tab.mode === viewMode)!.value}
        handleTabClick={(value) =>
          setViewMode(TABS.find((tab) => tab.value === value)!.mode)
        }
        options={TABS}
        classes="mt-4"
      />
      <div className="rounded-xl bg-neutral-50 pb-4 mt-4">
        <div className="mt-4">
          {viewMode === "insights" ? (
            <Summary
              {...bar}
              query={{
                orgId: organization.id,
                range,
                compared,
                basis: accountingSystem,
              }}
              compareTo={compareTo}
              onCompareTo={chooseCompareTo}
              onComparedRange={setComparedPick}
            />
          ) : (
            <Statistics {...bar} />
          )}
        </div>
        {viewMode === "activity" && (
          <div className="my-4">
            <TransactionSettings
              accountingSystem={accountingSystem}
              transactionTypeFilter={transactionTypeFilter}
              setTransactionTypeFilter={setTransactionTypeFilter}
              adminApprovalFilter={adminApprovalFilter}
              setAdminApprovalFilter={setAdminApprovalFilter}
              viewMode={viewMode}
            />
          </div>
        )}
        {viewMode === "activity" && transactionsBlock}
        {viewMode === "insights" && (
          <div className="mt-4 mx-4">
            <FinancialVisualizations
              revenue={revenue}
              cost={cost}
              orgId={organization.id}
              zone={zone}
              range={range}
              compared={compared}
              basis={accountingSystem}
              subjects={organization.subjects ?? []}
              isFinancialsLoading={financialsLoading}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default OrgPayments;
